import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = { navy:'#0D1B2A', teal:'#00B4D8', grey:'#6B7280', border:'#E5E7EB', success:'#10B981', warning:'#F59E0B', error:'#EF4444', purple:'#8B5CF6' };
const LEVELS = ['A1','A2','B1','B2','C1','C2'];
const LEVEL_COLORS = { A1:'#64748B', A2:'#3B82F6', B1:C.teal, B2:'#0EA5E9', C1:C.warning, C2:'#D97706' };

export default function AdminSupplyDemand() {
  const [tenant, setTenant] = useState(null);
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingTutors, setPendingTutors] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get('/api/admin/dashboard').catch(() => ({ data: {} })),
      api.get('/api/admin/students').catch(() => ({ data: { students: [] } })),
      api.get('/api/admin/tutors').catch(() => ({ data: { tutors: [] } })),
    ]).then(([dash, studs, tuts]) => {
      setTenant(dash.data?.tenant || null);
      setStudents(studs.data?.students || studs.data || []);
      const tutList = tuts.data?.tutors || tuts.data || [];
      setTutors(tutList.filter(t => t.account_status !== 'deleted' && (t.is_approved || t.application_status === 'approved')));
      setPendingTutors(tutList.filter(t => !t.is_approved && t.application_status !== 'rejected').length);
    }).finally(() => setLoading(false));
  }, []);

  // CEFR breakdown
  const byLevel = {};
  LEVELS.forEach(l => { byLevel[l] = { students: 0, tutors: 0 }; });
  students.forEach(s => {
    const l = s.cefr_level || 'A1';
    if (byLevel[l]) byLevel[l].students++;
  });
  tutors.forEach(t => {
    // tutors cover all levels below their expertise — simplified: count them at one level
    const l = 'B1'; // default assign
    if (byLevel[l]) byLevel[l].tutors++;
  });

  const totalStudents = students.length;
  const totalTutors = tutors.length;
  const ratio = totalTutors > 0 ? (totalStudents / totalTutors).toFixed(1) : '—';
  const health = totalTutors === 0 ? 'critical' : totalStudents / totalTutors > 15 ? 'warning' : 'good';

  const StatBox = ({ label, value, color, sub }) => (
    <div style={{ background:'#fff', borderRadius:12, padding:'20px 24px', boxShadow:'0 1px 4px rgba(0,0,0,0.08)', borderLeft:`4px solid ${color}` }}>
      <div style={{ fontSize:28, fontWeight:800, color }}>{value}</div>
      <div style={{ fontSize:14, fontWeight:600, color:C.navy, marginTop:2 }}>{label}</div>
      {sub && <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>{sub}</div>}
    </div>
  );

  return (
    <Layout role="tenant_admin" tenantColor={tenant?.primary_colour} tenantName={tenant?.school_name}>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ margin:'0 0 4px', fontSize:22, fontWeight:700, color:C.navy }}>Supply & Demand</h2>
        <p style={{ margin:0, color:C.grey, fontSize:14 }}>Real-time student demand vs tutor capacity at your school</p>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:C.grey }}>Loading data…</div>
      ) : (
        <>
          {/* Overview Stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap:16, marginBottom:24 }}>
            <StatBox label="Total Students" value={totalStudents} color={C.teal} />
            <StatBox label="Active Tutors" value={totalTutors} color={C.success} />
            <StatBox label="Student : Tutor" value={ratio + ':1'} color={health==='good'?C.success:health==='warning'?C.warning:C.error}
              sub={health==='good'?'Healthy':health==='warning'?'Moderate load':'No tutors — recruit now'} />
            <StatBox label="Pending Applications" value={pendingTutors} color={pendingTutors>0?C.warning:C.grey}
              sub={pendingTutors>0?'Review in Tutors tab':'No pending applications'} />
          </div>

          {/* CEFR Breakdown */}
          <div style={{ background:'#fff', borderRadius:12, padding:24, marginBottom:24, boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }}>
            <h3 style={{ margin:'0 0 20px', fontSize:16, fontWeight:700, color:C.navy }}>Student Distribution by CEFR Level</h3>
            {LEVELS.map(l => {
              const count = byLevel[l]?.students || 0;
              const pct = totalStudents > 0 ? Math.round(count / totalStudents * 100) : 0;
              return (
                <div key={l} style={{ marginBottom:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, alignItems:'center' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontWeight:700, fontSize:15, color:C.navy, minWidth:32 }}>{l}</span>
                      <span style={{ fontSize:13, color:C.grey }}>{count} student{count !== 1 ? 's' : ''}</span>
                    </div>
                    <span style={{ fontWeight:700, fontSize:13, color:LEVEL_COLORS[l] }}>{pct}%</span>
                  </div>
                  <div style={{ position:'relative', height:22, background:'#F3F4F6', borderRadius:11, overflow:'hidden' }}>
                    <div style={{ position:'absolute', left:0, top:0, height:'100%', width:pct + '%', background:LEVEL_COLORS[l], borderRadius:11, transition:'width 0.6s', minWidth: count > 0 ? 4 : 0 }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tutor Recruitment Section */}
          <div style={{ background:`linear-gradient(135deg, ${C.navy} 0%, #1e3a5f 100%)`, borderRadius:16, padding:32, color:'#fff', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:24 }}>
            <div>
              <h3 style={{ margin:'0 0 8px', fontSize:20, fontWeight:800 }}>Recruit New Tutors</h3>
              <p style={{ margin:'0 0 16px', opacity:0.8, fontSize:14, maxWidth:480, lineHeight:1.6 }}>
                Share your school's tutor application link to attract qualified English tutors. Applications are reviewed in the Tutors tab.
              </p>
              <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                <a
                  href={`https://${tenant?.subdomain || 'www'}.speakiq.uk/tutor-signup?school=${tenant?.id || ''}`}
                  target="_blank" rel="noreferrer"
                  style={{ display:'inline-block', padding:'12px 24px', background:C.teal, color:'#fff', borderRadius:8, fontWeight:700, fontSize:14, textDecoration:'none' }}>
                  🔗 Tutor Application Page ↗
                </a>
                <button
                  onClick={() => {
                    const url = `https://${tenant?.subdomain || 'www'}.speakiq.uk/tutor-signup?school=${tenant?.id || ''}`;
                    navigator.clipboard.writeText(url).then(() => alert('Link copied!'));
                  }}
                  style={{ padding:'12px 24px', background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.3)', color:'#fff', borderRadius:8, fontWeight:600, fontSize:14, cursor:'pointer' }}>
                  📋 Copy Link
                </button>
              </div>
            </div>
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:56 }}>👩‍🏫</div>
              <div style={{ fontSize:13, opacity:0.7, marginTop:4 }}>{totalTutors} active tutor{totalTutors !== 1 ? 's' : ''}</div>
            </div>
          </div>

          {totalStudents === 0 && totalTutors === 0 && (
            <div style={{ marginTop:16, background:'#FEF9C3', border:'1px solid #FDE047', borderRadius:10, padding:'14px 16px', fontSize:13, color:'#92400E' }}>
              <strong>Getting started:</strong> Register your first students and share the tutor application link to begin building your school's team.
            </div>
          )}
        </>
      )}
    </Layout>
  );
}
